import React, { useEffect } from 'react';

import { Box, Tooltip, useColorModeValue } from '@vimeo/bokeh';
import { translate } from '@vimeo/i18n-runtime';

import { StaffPickIcon } from './StaffPickIcon';
import { SpBadgeTracking, trackStaffPickBadgeEvent, useGetBpSessionId } from './tracking';

const DEFAULT_BADGE_RADIUS = 18;
const DEFAULT_MARGIN_RIGHT = 8;

/**
 * A link component to optionally wrap the icon when there is a link or
 * click handler passed to the badge component
 */
interface OptionalLinkProps {
  href?: string;
  onClick?: () => void;
  children: React.ReactNode;
}
const OptionalLink = ({ href, onClick, children, ...rest }: OptionalLinkProps) => {
  return href || onClick ? (
    <a
      style={{ display: 'inline-flex' }}
      href={href}
      onClick={onClick}
      target="_blank"
      rel="noreferrer"
      {...rest}
    >
      {children}
    </a>
  ) : (
    <>{children}</>
  );
};

interface StaffPickBadgeProps {
  radius?: number;
  href?: string;
  videoId: number;
  userUri: string;
  feature: SpBadgeTracking['feature'];
  marginRight?: number;
  onClick?: () => void;
}

export function StaffPickBadge({
  radius,
  href,
  videoId,
  userUri,
  feature,
  onClick,
  marginRight = DEFAULT_MARGIN_RIGHT,
}: StaffPickBadgeProps) {
  // BP tracking data
  const sessionId = useGetBpSessionId();
  let location: SpBadgeTracking['location'] =
    feature === 'profile' ? 'video_details_panel' : 'comments_panel';
  let trackingSpec = { videoId, userUri, sessionId, feature, location };

  // Track view event
  useEffect(() => {
    let trackingSpec = { videoId, userUri, sessionId, feature, location };
    trackStaffPickBadgeEvent({ event: 'view', ...trackingSpec });
  }, [videoId, userUri, sessionId, feature, location]);

  // Track click event and call optional onClick
  const onIconClick = () => {
    trackStaffPickBadgeEvent({ event: 'click', ...trackingSpec });
    if (onClick) {
      onClick();
    }
  };

  const circleFill = useColorModeValue('#000000', '#FFFFFF');
  const pathFill = useColorModeValue('#FFFFFF', '#000000');

  return (
    <Tooltip label={translate('View Staff Picks')} placement="top">
      <Box
        display="inline-flex"
        alignSelf="center"
        marginRight={marginRight}
        sx={{
          '& svg': {
            width: `${radius ?? DEFAULT_BADGE_RADIUS}`,
            height: `${radius ?? DEFAULT_BADGE_RADIUS}`,
          },
          '& circle': {
            fill: `${circleFill}`,
          },
          '& .path-1': {
            fillRule: 'evenodd',
          },
          '& .path-1,& .path-2': {
            fill: `${pathFill}`,
          },
        }}
      >
        <OptionalLink href={href} onClick={onIconClick} aria-label={translate('View Staff Picks')}>
          <StaffPickIcon />
        </OptionalLink>
      </Box>
    </Tooltip>
  );
}
